import React from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import { compose, withProps } from 'recompose'
import BaseButton from './BaseButton'

import styles from './Button.module.scss'

const defaultProps = {
  danger: false,
  disabled: false,
  fullWidth: false,
  minimal: false,
  primary: false,
  compact: false
}

const propTypes = {
  // use for destructive actions
  danger: PropTypes.bool,

  // refuse to be clicked or animate
  disabled: PropTypes.bool,

  // attach a class to the button so the container can position or modify
  className: PropTypes.string,

  // let the container determine the button width
  fullWidth: PropTypes.bool,

  // indicates that the click event caused an async action
  loading: PropTypes.bool,

  // drops the padding from the button, for use inline with text
  minimal: PropTypes.bool,

  // put a bright background on the button, for use in CTAs
  primary: PropTypes.bool,

  // give the button an icon to layout after it's children
  iconEnd: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),

  // give the button an icon to layout before it's children
  iconStart: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),

  // decrease the padding on the button
  compact: PropTypes.bool
}

const Button = compose(
  withProps(
    ({
      children,
      className,
      compact,
      danger,
      disabled,
      fullWidth,
      iconEnd: IconEnd,
      iconStart: IconStart,
      loading,
      minimal,
      primary
    }) => {
      return {
        className: classnames(
          styles.button,
          compact && styles.compact,
          danger && styles.danger,
          fullWidth && styles.fullWidth,
          minimal && styles.minimal,
          primary && styles.primary,
          loading && styles.loading,
          (IconEnd || IconStart) && !children && styles.iconOnly,
          className
        ),
        children: !loading && (
          <>
            {IconStart ? <IconStart className={styles.iconStart} /> : null}
            {children}
            {IconEnd ? <IconEnd className={styles.iconEnd} /> : null}
          </>
        ),
        disabled: loading || disabled
      }
    }
  )
)(
  ({
    forwardedRef,
    compact, // ignore
    danger, // ignore
    fullWidth, // ignore
    minimal, // ignore
    primary, // ignore
    iconEnd, // ignore
    iconStart, // ignore
    loading, // ignore
    ...restProps
  }) => <BaseButton ref={forwardedRef} {...restProps} />
)

Button.defaultProps = defaultProps
Button.propTypes = propTypes

export default React.forwardRef((props, ref) => (
  <Button {...props} forwardedRef={ref} />
))
